<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
      </div>
      <v-card-text class="pa-4" style="max-height: 70vh; overflow-y: auto">
        <label class="label-input">Section Name</label>
        <v-row>
          <v-col cols="12" md="8"><v-text-field v-model="fields.title" label="Name"/></v-col>
          <v-col cols="12" md="4"><v-checkbox v-model="fields.enableAnchor" label="Enable Anchor"/></v-col>
        </v-row>

        <div class="mt-4">
          <label class="label-input">Header</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.headerTitle" :config="editorConfig" />
            <v-row class="mt-3">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="fields.rating.value"
                  label="Average rating"
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  :rules="[v => (v >= 0.1 && v <= 5) || 'Average rating must be between 0.1 and 5.0']"
                />
              </v-col>
              <v-col cols="12" md="6" class="mt-2">
                <ColorPicker
                  v-model="fields.starColor"
                  name="konfortify-review-star-color"
                  label="Star color"
                  default-color="#39CA41"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mt-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <label class="label-input mb-0">Reviews</label>
            <v-btn small color="primary" @click="handleAddItem"><v-icon size="16">mdi-plus</v-icon>Add Review</v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.listItem" :key="index">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>{{ item.fullName || `Review ${index + 1}` }}</span>
                    <v-btn icon @click.stop="handleRemoveItem(index)"
                      ><v-icon color="#ff6c6c">mdi-delete</v-icon></v-btn
                    >
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field v-model="item.title" label="Title" />
                  <v-textarea v-model="item.content" label="Content" outlined rows="3" />
                  <v-text-field v-model="item.fullName" label="Full name" />
                  <label class="d-block mb-2">Avatar</label>
                  <UploadImage
                    :image="{ imageSrc: item.avatar }"
                    :height="`160`"
                    :typeUpload="'image'"
                    @callback="value => handleUploadAvatar(value, index)"
                    @callbackDelete="handleRemoveAvatar(index)"
                  />
                  <label class="d-block mt-5 mb-2">Stars</label>
                  <v-rating
                    :value="item.rating"
                    :color="fields.starColor"
                    background-color="#d9d9d9"
                    hover
                    length="5"
                    size="28"
                    @input="value => (item.rating = value)"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <SectionSettingsContainerFuild
          class="mt-6"
          :settings="fields.settings"
          :container-page="fields.containerPage"
          :showBackgroundImage="false"
          :showContainerPage="false"
        />
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions
          ><v-spacer /><v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn
          ><v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn></v-card-actions
        >
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import ColorPicker from './common/color-picker.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const defaultItem = () => ({
  title: 'Title',
  content: 'Content',
  avatar: 'https://cdn.skyfastimg.com/images/2026/08/19/image-6a8527765a90772d34b1a843.webp?w=200',
  fullName: 'Name',
  rating: 5,
});

export default {
  name: 'KonfortifyReviewGrid',
  components: { UploadImage, ColorPicker, SectionSettingsContainerFuild },
  props: { sectionEditor: { type: Object, required: true } },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Konfortify review grid',
        enableAnchor: false,
        headerTitle: '',
        rating: { value: 5 },
        starColor: '#39CA41',
        listItem: [],
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
          useBackgroundMobile: false,
          backgroundImageMobile: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(value) {
        if (value && value.sectionExtract) this.applyExtract(value.sectionExtract);
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        rating: { ...this.fields.rating, ...(extract.rating || {}) },
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        listItem: (extract.listItem || []).map(item => ({ ...defaultItem(), ...item })),
      };
    },
    handleAddItem() {
      this.fields.listItem.push(defaultItem());
    },
    handleRemoveItem(index) {
      this.fields.listItem.splice(index, 1);
    },
    handleUploadAvatar(value, index) {
      this.fields.listItem[index].avatar = (value && value.imageSrc) || '';
    },
    handleRemoveAvatar(index) {
      this.fields.listItem[index].avatar = '';
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionKonfortifyReviewGrid(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', { ...this.sectionEditor.section, title: this.fields.title, html });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.label-input {
  display: block;
  margin-bottom: 10px;
  color: #000;
  font-size: 18px;
  font-weight: 500;
}
.group-item {
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}
.header-modal,
.footer-modal {
  position: sticky;
  left: 0;
  z-index: 999;
  padding: 0 16px;
  background: #fff;
  box-shadow: 0 0 12px #0000000f;
}
.header-modal {
  top: 0;
}
.footer-modal {
  bottom: 0;
}
</style>
